import { PromotionDetails } from '@commerce-promotion-webcomponents/ui';
import { FAIcon, Toggle, useNextgenRouter, useTranslation } from '@nextgen-web-framework/all';
import {
  type Cart,
  getActiveCart,
  isGroupOrder,
  getUniquePromotions,
  getFiredPromotionFromEntries,
  getFiredPromotionFromSOPMetaData,
  cartApi,
  updateCart,
  PublishEvent,
  CartEventsEnum,
  CartConstantsEnum,
} from '@commerce-cart-webcomponents/sdk';
import { memo } from 'react';
import { useDispatch } from 'react-redux';
import { ConsoleLogger, StorageService } from '@nextgen-web-framework/core';
import { CartCouponsWrapper } from '../cart-coupon-wrapper/CartCouponsWrapper';
import { CartNotificationTheme, TOAST_TIMEOUT, useCartNotification, useFeatureFlag } from '../../../common';
import styles from './CartLineItemsFooter.module.scss';

export const CartLineItemsFooter = memo(
  ({ cartData, cartDetailsPage = false }: { cartData: Cart; cartDetailsPage?: boolean }) => {
    const t = useTranslation();
    const router = useNextgenRouter();
    const cartId = String(router.params?.cartId);
    const currentActiveCart = getActiveCart(cartData, cartId);
    const { promotionResults, entries = [], sopMetaData = [] } = currentActiveCart;
    const { createToast, deleteToast } = useCartNotification('cartLineItemsFooter');
    const showRemoveSuccess = t('cart.cartlineItemFooter.removeSuccess');
    const showPromotions =
      (!isGroupOrder(cartData.orderType) || cartDetailsPage) &&
      (Boolean(promotionResults?.applicablePromotions?.length) ||
        Boolean(promotionResults?.potentialPromotions?.length));
    const firedPromotionsFromEntries = getFiredPromotionFromEntries(entries);
    const firedPromotionsFromSOP = getFiredPromotionFromSOPMetaData(sopMetaData);

    const { showAuxiliaryToggleFlag } = useFeatureFlag();
    const uniqueFiredPromotions = getUniquePromotions(
      [...(promotionResults?.firedPromotions ?? []), ...firedPromotionsFromEntries, ...firedPromotionsFromSOP],
      showAuxiliaryToggleFlag,
    );
    const promoDetailsUpdate = ConfigService.getConfigs().NEXT_ENABLE_PROMO_DETAILS_UPDATE;
    const showCoupons = !isGroupOrder(cartData.orderType) || cartDetailsPage;
    const dispatch = useDispatch();

    const auxiliaryToggleHandler = async () => {
      try {
        const res = (await cartApi.updateCart(
          StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          currentActiveCart.subCartId as string,
          {
            removeAuxiliaryProducts: !currentActiveCart?.removeAuxiliaryProducts,
            version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
          },
        )) as Cart;
        if (!currentActiveCart?.removeAuxiliaryProducts) {
          createToast(
            {
              theme: CartNotificationTheme.SUCCESS,
              rightSeperator: false,
              content: showRemoveSuccess,
              clickIcon: 'times',
              icon: 'check-circle',
              timeOut: TOAST_TIMEOUT,
            },
            deleteToast,
          );
        }
        dispatch(updateCart(res as Cart));
        PublishEvent({
          cartEvent: CartEventsEnum.UpdateMiniCartIcon,
          cart: res as Cart,
        });
      } catch (e) {
        ConsoleLogger.log(e);
      }
    };
    return (
      <>
        {showAuxiliaryToggleFlag && currentActiveCart?.hasAuxiliaryItems && (
          <div className={`${styles['auxiliaryItemToggle']}`}>
            <FAIcon iconName={'globe-asia'} className={`${styles['auxiliaryItemToggle-globeIcon']}`} />
            <div className={`${styles['auxiliaryItemToggle-desc']}`}>
              <div className={`${styles['auxiliaryItemToggle-desc-title']}`}>
                {t('cart.cartLineItemsFooter.helpUsReducePlastic')}
              </div>
              <div className={`${styles['auxiliaryItemToggle-desc-subtitle']}`}>
                {currentActiveCart?.removeAuxiliaryProducts
                  ? t('cart.cartLineItemsFooter.ThankyouForNotAccepting')
                  : t('cart.cartLineItemsFooter.getASpoonandAPlasticBag')}
              </div>
            </div>
            <div className={`${styles['auxiliaryItemToggle-toggle']}`}>
              <Toggle onClick={() => auxiliaryToggleHandler()} checked={currentActiveCart?.removeAuxiliaryProducts} />
            </div>
          </div>
        )}
        {(showPromotions || showCoupons) && (
          <div data-testid="cart-line-item-footer" className={styles['footer-container']}>
            {showPromotions && (
              <div className={styles['commerce-cart-line-items-header-page-promotion-banner']}>
                <PromotionDetails
                  firedPromotions={uniqueFiredPromotions}
                  potentialPromotions={promotionResults?.potentialPromotions}
                  tabVariant={Boolean(promoDetailsUpdate)}
                />
              </div>
            )}
            {showCoupons && <CartCouponsWrapper cart={cartData} subCart={currentActiveCart ?? {}} />}
          </div>
        )}
      </>
    );
  },
);

CartLineItemsFooter.displayName = 'CartLineItemsFooter';
